/**
 * Original scheduled departure stays put when a trip slips.
 * `departureIso` / `estimatedStd` are the current time. `scheduledStd` and
 * `originalStd` are the first stored STD and are not rewritten by a slip.
 */
import { halifaxDateKey } from "./duty";
import type { Mission, SlipCause, SlipConfirmReason, SlipJournalEntry } from "./types";

export const SLIP_CONFIRM_MS = 24 * 60 * 60 * 1000;

let slipSeq = 0;

export function newSlipId(now: Date, missionId: string): string {
  slipSeq += 1;
  return `slip-${now.getTime().toString(36)}-${slipSeq.toString(36)}-${missionId}`;
}

export function newSlipBatchId(now: Date): string {
  slipSeq += 1;
  return `slip-batch-${now.getTime().toString(36)}-${slipSeq.toString(36)}`;
}

function isoOrEmpty(value: unknown): string {
  if (typeof value !== "string" || !value.trim()) return "";
  const t = new Date(value);
  if (Number.isNaN(t.getTime())) return "";
  return t.toISOString();
}

export function immutableStd(mission: Pick<Mission, "scheduledStd" | "originalStd" | "departureIso">): string {
  return isoOrEmpty(mission.scheduledStd) || isoOrEmpty(mission.originalStd) || isoOrEmpty(mission.departureIso);
}

export function estimatedStdOf(mission: Pick<Mission, "estimatedStd" | "departureIso">): string {
  return isoOrEmpty(mission.estimatedStd) || isoOrEmpty(mission.departureIso);
}

const CAUSES: readonly SlipCause[] = ["desk", "brain", "auto_resolve", "megan", "sim", "ferry", "revert"];

export function hydrateSlipEntry(raw: unknown): SlipJournalEntry | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const row = raw as Partial<SlipJournalEntry>;
  const id = typeof row.id === "string" ? row.id.trim() : "";
  const missionId = typeof row.missionId === "string" ? row.missionId.trim() : "";
  const from = isoOrEmpty(row.from);
  const to = isoOrEmpty(row.to);
  const at = isoOrEmpty(row.at);
  if (!id || !missionId || !from || !to || !at) return null;
  if (!CAUSES.includes(row.cause as SlipCause)) return null;
  return {
    id,
    missionId,
    from,
    to,
    cause: row.cause as SlipCause,
    causeMissionId: typeof row.causeMissionId === "string" && row.causeMissionId.trim() ? row.causeMissionId : null,
    at,
    batchId: typeof row.batchId === "string" && row.batchId.trim() ? row.batchId : id,
    revertedAt: isoOrEmpty(row.revertedAt) || null,
  };
}

export function hydrateSlipJournal(raw: unknown): SlipJournalEntry[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(hydrateSlipEntry).filter((row): row is SlipJournalEntry => Boolean(row));
}

export function collectSlipJournal(missions: readonly Mission[]): SlipJournalEntry[] {
  const out: SlipJournalEntry[] = [];
  const seen = new Set<string>();
  for (const mission of missions) {
    for (const row of mission.slips ?? []) {
      const hydrated = hydrateSlipEntry(row);
      if (!hydrated || seen.has(hydrated.id)) continue;
      seen.add(hydrated.id);
      out.push(hydrated);
    }
  }
  return out;
}

/** Put a saved journal back on trips that do not already carry those rows. */
export function attachSlipJournal(missions: Mission[], journal: readonly SlipJournalEntry[]): Mission[] {
  if (!journal.length) return missions;
  const byMission = new Map<string, SlipJournalEntry[]>();
  for (const row of journal) {
    const list = byMission.get(row.missionId) ?? [];
    list.push(row);
    byMission.set(row.missionId, list);
  }
  return missions.map((mission) => {
    const own = hydrateSlipJournal(mission.slips);
    if (own.length) return mission.slips === own ? mission : { ...mission, slips: own };
    const extra = byMission.get(mission.id);
    return extra?.length ? { ...mission, slips: extra } : mission;
  });
}

export function slipNeedsConfirm(
  mission: Pick<Mission, "scheduledStd" | "originalStd" | "departureIso">,
  toIso: string,
): SlipConfirmReason | null {
  const origin = immutableStd(mission);
  const to = isoOrEmpty(toIso);
  if (!origin || !to) return null;
  const fromMs = new Date(origin).getTime();
  const toMs = new Date(to).getTime();
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs)) return null;
  if (Math.abs(toMs - fromMs) > SLIP_CONFIRM_MS) return "over_24h";
  const fromDay = halifaxDateKey(new Date(origin));
  const toDay = halifaxDateKey(new Date(to));
  if (fromDay && toDay && fromDay !== toDay) return "other_duty_day";
  return null;
}

export function stampEstimatedStd(mission: Mission, departureIso: string): Mission {
  const next = isoOrEmpty(departureIso) || mission.departureIso;
  const scheduledStd = immutableStd(mission) || next;
  const originalStd = isoOrEmpty(mission.originalStd) || scheduledStd;
  const legs = mission.legs?.length
    ? mission.legs.map((leg, index) => (index === 0 ? { ...leg, departureIso: next } : leg))
    : mission.legs;
  return {
    ...mission,
    scheduledStd,
    originalStd,
    estimatedStd: next,
    departureIso: next,
    legs,
  };
}

export function appendSlip(
  mission: Mission,
  input: {
    from: string;
    to: string;
    cause: SlipCause;
    causeMissionId?: string | null;
    at: string;
    batchId: string;
  },
): Mission {
  const from = isoOrEmpty(input.from);
  const to = isoOrEmpty(input.to);
  if (!from || !to || from === to) return mission;
  const entry: SlipJournalEntry = {
    id: newSlipId(new Date(input.at), mission.id),
    missionId: mission.id,
    from,
    to,
    cause: input.cause,
    causeMissionId: input.causeMissionId ?? null,
    at: input.at,
    batchId: input.batchId,
    revertedAt: null,
  };
  return { ...mission, slips: [...(mission.slips ?? []), entry] };
}

export function recordSlip(
  mission: Mission,
  departureIso: string,
  meta: { cause: SlipCause; causeMissionId?: string | null; at: string; batchId: string },
): Mission {
  const from = mission.departureIso;
  const stamped = stampEstimatedStd(mission, departureIso);
  return appendSlip(stamped, {
    from,
    to: stamped.departureIso,
    cause: meta.cause,
    causeMissionId: meta.causeMissionId,
    at: meta.at,
    batchId: meta.batchId,
  });
}

export type SlipConfirmRequest = {
  missionId: string;
  from: string;
  to: string;
  reason: SlipConfirmReason;
  causeMissionId: string | null;
};

export function slipConfirmNotice(reason: SlipConfirmReason): string {
  return reason === "over_24h"
    ? "SLIP  that move is more than 24 hours from the original STD. Confirm to apply."
    : "SLIP  that move leaves the duty day. Confirm to apply.";
}

export function confirmRequestFor(
  mission: Mission,
  toIso: string,
  causeMissionId: string | null = null,
): SlipConfirmRequest | null {
  const reason = slipNeedsConfirm(mission, toIso);
  if (!reason) return null;
  return {
    missionId: mission.id,
    from: mission.departureIso,
    to: isoOrEmpty(toIso) || toIso,
    reason,
    causeMissionId,
  };
}

export function unrevertedSlips(mission: Pick<Mission, "slips">): SlipJournalEntry[] {
  return (mission.slips ?? []).filter((row) => !row.revertedAt);
}

/**
 * Put the current STD back on the original scheduled STD.
 * The original itself is not rewritten. Linked rows in the same slip batch move with it.
 */
export function revertSlipMissions(
  missions: readonly Mission[],
  missionId: string,
  now = new Date(),
): { missions: Mission[]; restored: number } {
  const seed = missions.find((row) => row.id === missionId);
  if (!seed) return { missions: [...missions], restored: 0 };
  const batches = new Set(unrevertedSlips(seed).map((row) => row.batchId));
  const ids = new Set<string>([missionId]);
  if (batches.size) {
    for (const row of missions) {
      if ((row.slips ?? []).some((slip) => !slip.revertedAt && batches.has(slip.batchId))) ids.add(row.id);
    }
  }
  const at = now.toISOString();
  let restored = 0;
  const next = missions.map((mission) => {
    if (!ids.has(mission.id)) return mission;
    const target = immutableStd(mission);
    const current = isoOrEmpty(mission.departureIso);
    const marked: Mission = {
      ...mission,
      slips: (mission.slips ?? []).map((slip) => (slip.revertedAt ? slip : { ...slip, revertedAt: at })),
    };
    if (!target || !current || target === current) return marked;
    restored += 1;
    const stamped = stampEstimatedStd(marked, target);
    // stamp keeps the original; force the clocks back onto that original.
    const restoredMission: Mission = {
      ...stamped,
      scheduledStd: target,
      originalStd: isoOrEmpty(mission.originalStd) || target,
      estimatedStd: target,
      departureIso: target,
    };
    return appendSlip(restoredMission, {
      from: current,
      to: target,
      cause: "revert",
      causeMissionId: missionId,
      at,
      batchId: newSlipBatchId(now),
    });
  });
  return { missions: next, restored };
}
